import { Link } from 'react-router-dom';

const statusItems = [
  { label: 'Network Uptime', value: '99.98%', dot: 'bg-emerald-400' },
  { label: 'Backup Systems', value: 'All Verified', dot: 'bg-emerald-400' },
  { label: 'Threat Detection', value: 'Active', dot: 'bg-emerald-400' },
  { label: 'Cloud Sync', value: 'Live', dot: 'bg-emerald-400' },
];

const metrics = [
  { label: 'Avg Response', value: '< 4 min' },
  { label: 'Recovery Time', value: '< 15 min' },
  { label: 'Uptime SLA', value: '99.9%' },
];

const Hero = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-[72px]">

      {/* ── Background Image ── */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/a4f0d24692f01ba89ef6d811341c9410.jpg"
          alt="Data center infrastructure"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* ── Layered overlays for depth ── */}
      {/* Base dark overlay */}
      <div className="absolute inset-0 bg-slate-950/70" />
      {/* Left-side deepening gradient (makes text pop) */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/50 to-slate-950/20" />
      {/* Top fade for navbar blend */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-slate-950/60 to-transparent" />
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
      {/* Teal atmospheric glow — top right */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-500/10 blur-[120px] pointer-events-none" />

      {/* ── Content ── */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
        <div className="grid lg:grid-cols-2 gap-12 xl:gap-20 items-center">

          {/* ─── LEFT: Copy ─── */}
          <div className="order-2 lg:order-1">
            {/* Eyebrow pill */}
            <div className="inline-flex items-center gap-2.5 border border-teal-400/30 bg-teal-500/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse flex-shrink-0" />
              <span className="text-teal-300 text-xs font-bold tracking-widest uppercase">
                DFW Business Continuity Partner
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-[2.7rem] lg:text-[3.2rem] xl:text-[3.8rem] font-extrabold text-white leading-[1.02] tracking-tight mb-6">
              Never Let Downtime,<br />
              Data Loss, or IT Failures<br />
              <span className="text-teal-400">Stop Your Business</span>
            </h1>

            {/* Subheadline */}
            <p className="text-slate-300 text-lg leading-relaxed mb-10 max-w-md">
              Managed IT, backup, and disaster recovery for businesses that rely on uptime.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-14">
              <Link
                to="/locations"
                className="inline-flex items-center justify-center gap-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold px-8 py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Schedule a Consultation
                <i className="ri-arrow-right-line text-base" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 bg-white/8 hover:bg-white/12 backdrop-blur-sm text-white text-sm font-semibold px-8 py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                View Services
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-10 pt-8 border-t border-white/10">
              {[
                { value: '24/7', label: 'Live Local Support' },
                { value: '15+', label: 'Years in Business' },
                { value: '100%', label: 'Never Outsourced' },
              ].map((stat) => (
                <div key={stat.value}>
                  <p className="text-3xl font-extrabold text-white tracking-tight">{stat.value}</p>
                  <p className="text-slate-400 text-xs mt-1 font-medium tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ─── RIGHT: Glass Dashboard Panel ─── */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="w-full max-w-[460px] relative">

              {/* Outer glow ring */}
              <div className="absolute -inset-4 bg-teal-500/12 blur-2xl rounded-3xl pointer-events-none" />
              <div className="absolute -inset-1 bg-gradient-to-br from-teal-500/20 via-transparent to-teal-500/5 rounded-2xl pointer-events-none" />

              {/* Main glass panel */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10"
                style={{ background: 'rgba(2, 8, 20, 0.75)', backdropFilter: 'blur(24px)' }}
              >

                {/* Panel header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                    <span className="text-slate-200 text-xs font-bold tracking-widest uppercase">System Monitor</span>
                    <span className="text-slate-600 text-xs">· Live</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
                  </div>
                </div>

                {/* Infrastructure health bars */}
                <div className="px-5 py-5 border-b border-white/6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-500 text-[10px] font-bold tracking-widest uppercase">Infrastructure Health</span>
                    <span className="text-emerald-400 text-[10px] font-extrabold tracking-wide uppercase flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse inline-block" />
                      All Systems Operational
                    </span>
                  </div>
                  <div className="space-y-3.5">
                    {[
                      { name: 'Server Uptime', pct: 99.98 },
                      { name: 'Backup Integrity', pct: 100 },
                      { name: 'Network Health', pct: 98.4 },
                      { name: 'Cloud Sync', pct: 99.1 },
                    ].map((bar) => (
                      <div key={bar.name}>
                        <div className="flex justify-between mb-1.5">
                          <span className="text-slate-400 text-xs">{bar.name}</span>
                          <span className="text-teal-300 text-xs font-bold">{bar.pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-400"
                            style={{ width: `${bar.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status grid */}
                <div className="px-5 py-4 border-b border-white/6">
                  <div className="grid grid-cols-2 gap-2">
                    {statusItems.map((item) => (
                      <div
                        key={item.label}
                        className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 border border-white/6"
                        style={{ background: 'rgba(255,255,255,0.03)' }}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.dot}`} />
                        <div>
                          <p className="text-slate-500 text-[10px] leading-none mb-0.5">{item.label}</p>
                          <p className="text-slate-100 text-xs font-bold">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Metrics */}
                <div className="px-5 py-4 border-b border-white/6">
                  <div className="grid grid-cols-3 divide-x divide-white/6">
                    {metrics.map((m) => (
                      <div key={m.label} className="text-center px-2">
                        <p className="text-teal-400 text-base font-extrabold tracking-tight">{m.value}</p>
                        <p className="text-slate-600 text-[10px] mt-0.5 leading-tight">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity log */}
                <div className="px-5 py-4">
                  <p className="text-slate-600 text-[10px] uppercase tracking-widest font-bold mb-3">Recent Activity</p>
                  <div className="space-y-2.5">
                    {[
                      { time: '08:42', msg: 'Backup verification completed — all clear' },
                      { time: '07:15', msg: 'Threat scan complete — no anomalies detected' },
                      { time: '06:00', msg: 'Automated patch cycle applied successfully' },
                    ].map((entry, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-xs mt-0.5 flex-shrink-0" />
                        <span className="text-slate-400 text-[11px] leading-tight">
                          <span className="text-slate-500 font-semibold mr-1.5">{entry.time}</span>
                          {entry.msg}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badge — glass style */}
              <div
                className="absolute -bottom-5 -left-5 rounded-xl px-4 py-3 border border-white/10"
                style={{ background: 'rgba(2, 8, 20, 0.85)', backdropFilter: 'blur(16px)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-500/20 border border-teal-500/30 rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-fill text-teal-400 text-base" />
                  </div>
                  <div>
                    <p className="text-white text-xs font-extrabold leading-none mb-0.5">Protected 24/7</p>
                    <p className="text-slate-500 text-[10px]">Response in &lt; 4 min</p>
                  </div>
                </div>
              </div>

              {/* Top-right floating metric badge */}
              <div
                className="absolute -top-4 -right-2 rounded-xl px-4 py-2.5 border border-white/10"
                style={{ background: 'rgba(2, 8, 20, 0.85)', backdropFilter: 'blur(16px)' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                  <p className="text-slate-200 text-xs font-bold">99.9% Uptime SLA</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
